import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import {
  Toast,
  Toaster,
  ToastTitle,
  useId,
  useToastController,
  type ToastIntent,
} from "@fluentui/react-components";

export interface ToastOptions {
  /** Override the auto-dismiss duration in milliseconds. Defaults to 5000. */
  timeout?: number;
}

export interface ToastContextValue {
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  warning: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const DEFAULT_TIMEOUT = 5000;

export const ToastProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);

  const dispatch = useCallback(
    (message: string, intent: ToastIntent, timeout: number) => {
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent, timeout },
      );
    },
    [dispatchToast],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message, options) =>
        dispatch(message, "success", options?.timeout ?? DEFAULT_TIMEOUT),
      error: (message, options) =>
        dispatch(message, "error", options?.timeout ?? DEFAULT_TIMEOUT),
      warning: (message, options) =>
        dispatch(message, "warning", options?.timeout ?? DEFAULT_TIMEOUT),
      info: (message, options) =>
        dispatch(message, "info", options?.timeout ?? DEFAULT_TIMEOUT),
    }),
    [dispatch],
  );

  return (
    <ToastContext.Provider value={value}>
      <Toaster toasterId={toasterId} position="bottom-start" pauseOnHover />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
